<template>
  <div class="relative">
    <label class="block text-left text-2xs font-bold uppercase text-dark-blue">
      {{ label }}
    </label>
    <input
      id="placeInput"
      ref="input"
      v-model="address"
      type="text"
      class="google-autocomplete mt-1 w-full border border-transparent bg-off-white text-sm text-dark-gray focus:bg-white focus:text-body-color"
      placeholder="Enter Address"
      @keyup.up="moveSelectionUp"
      @keyup.down="moveSelectionDown"
    />
    <svg
      v-if="address !== ''"
      class="close-icon cursor-pointer"
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      @click="onClearSelection"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M7.42481 5.30349L2.12132 0L0 2.12132L5.30349 7.42481L0.000383496 12.7279L2.1217 14.8492L7.42481 9.54613L12.7279 14.8492L14.8492 12.7279L9.54613 7.42481L14.8496 2.12132L12.7283 0L7.42481 5.30349Z"
        fill="#313545"
      />
    </svg>
    <svg
      v-else
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="magnifying-glass"
    >
      <path
        d="M12.0195 12.7188L16.4995 17.1987"
        stroke="#6D7181"
        stroke-width="2"
      />
      <circle
        cx="8.17989"
        cy="8.23897"
        r="6.67989"
        stroke="#6D7181"
        stroke-width="2"
      />
    </svg>
    <div
      v-if="active"
      class="pac-container"
    >
      <p
        v-for="(address, idx) in suggestions"
        :key="idx"
        class="pac-item text-left hover:bg-gray-100"
        :class="{ 'bg-blue-100': selectedIndex == idx }"
        @click="onSelectAddress(address)"
      >
        {{ address.description }}
      </p>
    </div>
  </div>
</template>

<script>
const ADDRESS_COMPONENTS = {
  subpremise: 'short_name',
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  administrative_area_level_2: 'long_name',
  country: 'long_name',
  postal_code: 'short_name',
};

/* global google */
import debounce from 'lodash.debounce';
var service = null;
var autocomplete = null;
export default {
  name: 'GoogleAutocompletePlaces',

  props: {
    label: {
      default: '',
      type: String,
      required: false,
    },
  },

  emits: ['placechanged'],

  data() {
    return {
      address: '',
      active: false,
      suggestions: [],
      placeId: null,
      selectedIndex: undefined,
      selectionMade: false,
      fetchPredictions: null,
    };
  },

  watch: {
    address(newVal) {
      if (newVal?.length === 0) {
        this.suggestions = [];
        this.active = false;
        this.placeId = null;
      } else if (!this.selectionMade) {
        this.fetchPredictions(newVal);
      } else {
        this.selectionMade = false;
      }
    },
  },
  created() {
    const initMap = setInterval(function () {
      let alreadyLoaded = google?.maps?.places;
      if (alreadyLoaded) {
        service = new google.maps.places.AutocompleteService();
        autocomplete = new google.maps.places.PlacesService(
          document.createElement('div')
        );
        clearInterval(initMap);
      }
    }, 50);
    this.fetchPredictions = debounce(this.fetchPlacePredictions, 500);
  },
  mounted() {
    document.addEventListener('click', this.outsideClickListener);
    document.addEventListener('keydown', this.enterEvent);
  },

  unmounted() {
    document.removeEventListener('click', this.outsideClickListener);
    document.removeEventListener('keydown', this.enterEvent);
  },

  methods: {
    fetchPlacePredictions(input) {
      if (!service) {
        console.error('Google Maps Autocomplete Service not initialized.');
        return;
      }
      service.getPlacePredictions(
        {
          input: input,
          types: ['address'],
          componentRestrictions: { country: ['ca', 'us'] },
        },
        (predictions, status) => {
          if (status === 'OK') {
            const placeIdIsNotInUse = predictions?.some(
              (prediction) => prediction?.place_id !== this?.placeId
            );
            if (placeIdIsNotInUse) {
              this.active = true;
              this.suggestions = predictions;
            }
          }
        }
      );
    },
    async getByPlaceIdInfoAddress(placeId) {
      return new Promise((resolve, reject) => {
        autocomplete.getDetails({ placeId: placeId }, (place, status) => {
          if (status == google.maps?.places?.PlacesServiceStatus.OK) {
            resolve(place);
          } else {
            reject(status);
          }
        });
      });
    },

    enterEvent(event) {
      if (event.key === 'Enter') {
        this.onSelectAddress(this.suggestions?.[this.selectedIndex]);
      }
      if (event.key === 'Escape') {
        this.active = false;
      }
    },

    formatResult(place) {
      let returnData = {};
      for (let i = 0; i < place?.address_components?.length; i++) {
        let addressType = place?.address_components[i].types[0];

        if (ADDRESS_COMPONENTS[addressType]) {
          let val =
            place?.address_components?.[i]?.[ADDRESS_COMPONENTS[addressType]];
          returnData[addressType] = val;
        }
      }

      returnData['latitude'] = place.geometry?.location?.lat();
      returnData['longitude'] = place.geometry?.location?.lng();

      if (!returnData?.['street_number'] || !returnData?.['route']) {
        returnData['street1_backup_value'] = this.address?.split(',')?.[0];
      }
      if (!returnData?.['locality']) {
        returnData['locality_backup_value'] = this.address
          ?.split(',')?.[1]
          ?.trim();
      }
      return returnData;
    },

    async onSelectAddress(address) {
      if (address !== '') {
        this.address = address?.description;
        this.selectionMade = true;
        this.active = false;
        this.placeId = address?.place_id;
        try {
          const place = await this.getByPlaceIdInfoAddress(address?.place_id);
          this.$emit('placechanged', this.formatResult(place));
        } catch (error) {
          console.error('Error fetching place details:', error);
        }
      }
    },

    onClearSelection() {
      this.address = '';
      this.$emit('placechanged', '');
    },

    outsideClickListener(event) {
      if (event.target.id !== 'placeInput') {
        this.active = false;
      }
    },

    moveSelectionUp() {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    },

    moveSelectionDown() {
      if (this.selectedIndex === undefined) {
        this.selectedIndex = 0;
      } else if (this.selectedIndex < this.suggestions.length - 1) {
        this.selectedIndex++;
      }
    },
  },
};
</script>

<style scoped>
.pac-container:after {
  background-image: none;
  display: none;
}

.pac-container {
  position: absolute;
  z-index: 15;
  background-color: white;
  margin-top: -7px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  box-shadow: none;
  border: 1px solid black;
  border-top: 0;
  width: calc(100% - 2px);
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  box-sizing: content-box;
  @apply text-gray-over-dark;
}

.pac-item {
  border: none;
  padding: 9px 14px;
  line-height: 21px;
  font: inherit;
  cursor: pointer;
  @apply text-gray-over-dark;
}

.google-autocomplete {
  outline: none;
  border-radius: 5px;
  padding: 16px 36px 16px 16px;
}

.google-autocomplete:focus {
  outline: none;
  border: 1px solid black !important;
}

.google-autocomplete:hover {
  border: 1px solid #c7cbdb;
}

.google-autocomplete:focus + .magnifying-glass > path,
.google-autocomplete:focus + .magnifying-glass > circle {
  stroke: #313545;
}

.magnifying-glass {
  position: absolute;
  top: 36px;
  right: 17px;
}

.close-icon {
  position: absolute;
  top: 36px;
  right: 17px;
}
</style>
